// services/configurationService.js
import api from "./api";

// List all items with pending changes with pagination support
export const getConfigurationChanges = async (
  search = "",
  schemaName = null,
  page = 1,
  pageSize = 20
) => {
  try {
    const params = ({
      search,
      page,
      page_size: pageSize,
      ...(schemaName && { client: schemaName }),
    });

    const response = await api.get("/configuration/", { params });
    return response.data;
  } catch (error) {
    console.error("Error fetching configuration changes:", error);
    throw error;
  }
};

// Execute configuration changes (dry run or commit)
export const executeConfiguration = async (
  isDryRun = true,
  probes = null,
  sites = null,
  tunnels = null,
  loadsheddingareas = null,
  blackboxes = null,
  all = null,
  alloys = null,
  wifi_profiles = null,
  captures = null,
  ssh_collectors = null,
  host_availabilities = null,
  network_devices = null,
  schemaName = null,
) => {
  try {
    const payload = {
      is_dry_run: isDryRun,
      ...(probes?.length > 0 && { probes }),
      ...(sites?.length > 0 && { sites }),
      ...(tunnels?.length > 0 && { tunnels }),
      ...(loadsheddingareas?.length > 0 && { loadsheddingareas }),
      ...(blackboxes?.length > 0 && { blackboxes }),
      ...(all?.length > 0 && {all}),
      ...(alloys?.length > 0 && { alloys }),
      ...(wifi_profiles?.length > 0 && { wifi_profiles }),
      ...(captures?.length > 0 && { captures }),
      ...(ssh_collectors?.length > 0 && { ssh_collectors }),
      ...(host_availabilities?.length > 0 && { host_availabilities }),
      ...(network_devices?.length > 0 && { network_devices }),
      ...(schemaName && { schemaName }),
    };

    console.log("Configuration payload:", payload);
    const response = await api.post("/configuration/execute/", payload, {
      params: {
        ...(schemaName && { client: schemaName }),
      },
    }
    );
    return response.data;
  } catch (error) {
    console.error(`Error executing configuration:`, error);
    throw error;
  }
};

// Execute bulk configuration changes
export const executeBulkConfiguration = async (schemaName = null) => {
  try {
    // Change from /configuration/bulk-execute/ to /configuration/bulk_execute/
    const response = await api.post("/configuration/bulk_execute/", {
      params: {
        ...(schemaName && { client: schemaName }),
      },
    });
    console.log("Bulk configuration response:", response.data);

    if (!response.data || !response.data.file_request_id) {
      throw new Error("Invalid response from bulk configuration endpoint");
    }

    return response.data;
  } catch (error) {
    console.error("Error executing bulk configuration:", error);
    // Add more detailed error logging
    console.error("Error details:", {
      status: error.response?.status,
      url: error.config?.url,
      data: error.response?.data,
    });

    if (error.response?.data?.error) {
      throw new Error(error.response.data.error);
    }
    throw error;
  }
};

// Get configuration counts
export const getConfigurationCounts = async (schemaName = null) => {
  try {
    const response = await api.get("/configuration/counts/", {
      params: {
        ...(schemaName && { client: schemaName }),
      },
    });
    return response.data;
  } catch (error) {
    console.error("Error fetching configuration counts:", error);
    throw error;
  }
};

// Get details for a specific item
export const getConfigurationDetails = async (type, id, schemaName = null) => {
  try {
    const response = await api.get(`/configuration/details/${type}/${id}/`, {
      params: {
        ...(schemaName && { client: schemaName }),
      },
    });
    return response.data;
  } catch (error) {
    console.error("Error fetching configuration details:", error);
    throw error;
  }
};


export const denyConfiguration = async (type, id, schemaName = null) => {
  try {
    const response = await api.post(`/configuration/deny/${type}/${id}/`, null, {
      params: {
        ...(schemaName && { client: schemaName }),
      },
    });
    return response.data;
  } catch (error) {
    console.error("Error fetching configuration details:", error);
    throw error;
  }
};

// Get configuration status
export const getConfigurationStatus = async (fileRequestId, schemaName = null) => {
  try {
    const response = await api.get(`/configuration/${fileRequestId}/status/`, {
      params: {
        ...(schemaName && { client: schemaName }),
      },
    });
    return response.data;
  } catch (error) {
    console.error("Error fetching configuration status:", error);
    throw error;
  }
};

// Dismiss an abandoned/failed SaltStateFileRequest so the UI re-enables
// approve buttons without DB surgery. The backend refuses to dismiss
// queued/running/completed rows.
export const dismissConfigurationRequest = async (fileRequestId, schemaName = null) => {
  try {
    const response = await api.post(
      `/configuration/${fileRequestId}/dismiss/`,
      null,
      {
        params: {
          ...(schemaName && { client: schemaName }),
        },
      }
    );
    return response.data;
  } catch (error) {
    console.error("Error dismissing configuration request:", error);
    throw error;
  }
};


// Get configuration status
export const getRelatedConfigDetails = async (id, schemaName = null) => {
  try {
    const response = await api.get(`/configuration/related/${id}/`, {
      params: {
        ...(schemaName && { client: schemaName }),
      },
    });
    return response.data;
  } catch (error) {
    console.error("Error fetching configuration status:", error);
    throw error;
  }
};

// Optional: Add polling function for status
export const pollConfigurationStatus = async (
  fileRequestId,
  interval = 5000,
  schemaName = null,
) => {
  try {
    return new Promise((resolve, reject) => {
      const poll = async () => {
        try {
          const status = await getConfigurationStatus(fileRequestId, {
            params: {
              ...(schemaName && { client: schemaName }),
            },
          });
          if (status.state === "completed" || status.state === "failed") {
            resolve(status);
          } else {
            setTimeout(poll, interval);
          }
        } catch (error) {
          reject(error);
        }
      };
      poll();
    });
  } catch (error) {
    console.error("Error polling configuration status:", error);
    throw error;
  }
};
